import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  Provider,
  Accessibility,
  ProviderTitle,
} from '@apps/shared/src/providerSearch/types/providerSearch';
import { colors } from '../../style/colors';
import { radiusDescriptions } from '../api/providers';

import CategoryAndTitle from './CategoryAndTitle';
import PhoneNumber from './PhoneNumber';
import ProviderAddress from './ProviderAddress';

type Props = {
  provider: Provider;
  isFirstCard: boolean;
  isLastCard: boolean;
  category: string;
  title: string;
  selectedAccessibilities: Accessibility[];
};

const StyledCard = styled(Card)<{ borderColor: string; isLastCard: boolean }>(
  ({ borderColor, isLastCard }) => ({
    borderRadius: '.5rem',
    margin: '0.5rem 0.5rem 0 0.5rem',
    marginBottom: isLastCard ? '2rem' : '0.25rem',
    borderLeftWidth: '5px',
    borderLeftStyle: 'solid',
    borderLeftColor: borderColor,
    width: '100%',
    height: 'fit-content',
  })
);

const CardHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const OrganizationName = styled(Typography)({
  fontWeight: 'bold',
  color: '#36383D',
  margin: '0.5rem 0 0.8rem 0rem',
  lineHeight: 1.1,
});

const CardFooter = styled('div')({
  color: colors.greyDark,
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  '&:last-child': {
    paddingBottom: '16px',
  },
});

const StyledInfoIcon = styled(InfoIcon)({
  cursor: 'pointer',
  marginLeft: '5px',
  marginBottom: '8px',
  color: '#888787',
});

const StyledTooltip = styled(Tooltip)({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#FFFFFF',
    padding: '5px 10px',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center',
    color: '#888787',
    border: '1px solid #ddd',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
});

export function determineCardMargin(isFirstOrLastCard: boolean): string {
  return isFirstOrLastCard ? '2rem' : '0.25rem';
}

export function formatDistance(distance: number): string {
  if (distance === 0) return radiusDescriptions[0];
  if (distance <= 5) return radiusDescriptions[5];
  const rounded = Math.round(distance / 5) * 5;
  return `~ ${rounded.toFixed(0)} miles away`;
}

function determineBorderColor(label1: string, label2: string | undefined): string {
  if (label2 === 'Contracted') {
    return '#4caf50'; // Green
  }
  if (label1 === 'Unknown') {
    return '#9e9e9e'; // Grey
  }
  if (label1 === 'Caution') {
    return '#ffeb3b'; // Yellow
  }
  if (label1 === 'Not Recommended') {
    return '#f44336'; // Red
  }
  return '#000'; // Default
}

export default function ProviderCard({
  provider,
  isFirstCard: _isFirstCard,
  isLastCard,
  category,
  title,
  selectedAccessibilities: _selectedAccessibilities,
}: Props): JSX.Element {
  const { name, address1, address2, city, state, zip, phone, label1, label2, distance } = provider;

  const borderColor = determineBorderColor(provider.label1, provider.label2);

  let tooltipText = '';
  if (label2 === 'Contracted') {
    tooltipText = ProviderTitle.Green;
  } else if (label1 === 'Unknown') {
    tooltipText = ProviderTitle.Grey;
  } else if (label1 === 'Caution') {
    tooltipText = ProviderTitle.Yellow;
  } else if (label1 === 'Not Recommended') {
    tooltipText = ProviderTitle.Red;
  }

  const formattedDistance = formatDistance(distance);

  return (
    <StyledCard
      data-testid="ProviderCard"
      variant="outlined"
      borderColor={borderColor}
      isLastCard={isLastCard}
    >
      <StyledCardContent>
        <CardHeader>
          <OrganizationName>{name}</OrganizationName>
          <StyledTooltip title={tooltipText} placement="top" arrow>
            <StyledInfoIcon />
          </StyledTooltip>
        </CardHeader>
        <ProviderAddress
          name={name}
          address1={address1 || ''}
          address2={address2}
          city={city}
          state={state}
          zip={zip}
        />
        <CategoryAndTitle category={category} title={title} />
        <CardFooter>
          <PhoneNumber number={phone} />
          <Typography variant="body2">{formattedDistance}</Typography>
        </CardFooter>
      </StyledCardContent>
    </StyledCard>
  );
}
